import React from 'react';
import {
  MoreInfoOutlined,
  TickMarkOutlined,
  WarningAltOutlined,
  WarningOutlined,
} from '@duxton-web/icons';
import { Flex, Tooltip, Typography } from '@duxton-web/ui';

import {
  MetricWithDimensionFields,
  PopulationDerivedFilter,
  PrepopulateWarning,
} from '../../constants/analysis';
import {
  getPopulationFilterStatus,
  getSourceFacetLabel,
  getWarningReason,
} from './population-filter-utils';

const { Text } = Typography;

// Re-export utilities for backward compatibility
export {
  applyPopulationFiltersToMetric,
  cleanMetricForRepopulation,
  getPopulationFilterStatus,
  getSourceFacetLabel,
  needsPopulationFilters,
  processMetricForPopulationFilters,
  processMetricsForPopulationFilters,
  wasPopulationFiltersProcessed,
} from './population-filter-utils';

// Success badge - all constraints applied
const SuccessStatusBadge: React.FC<{
  appliedFilters: PopulationDerivedFilter[];
}> = ({ appliedFilters }) => {
  const filterLabels = appliedFilters
    .map((f) => getSourceFacetLabel(f.source_facet))
    .join(', ');

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '4px 8px',
        backgroundColor: '#e6f7e9',
        border: '1px solid #52c41a',
        borderRadius: 4,
        fontSize: 12,
        color: '#237804',
      }}
    >
      <TickMarkOutlined style={{ fontSize: 12 }} />
      Population filters auto-applied ({filterLabels})
    </div>
  );
};

// Partial badge - some constraints applied, some failed
const PartialStatusBadge: React.FC<{
  appliedFilters: PopulationDerivedFilter[];
  warnings: PrepopulateWarning[];
}> = ({ appliedFilters, warnings }) => {
  const appliedLabels = appliedFilters
    .map((f) => getSourceFacetLabel(f.source_facet))
    .join(', ');

  const tooltipContent = (
    <div style={{ maxWidth: 300 }}>
      <Text strong style={{ color: '#fff', display: 'block', marginBottom: 8 }}>
        Partially applied population filters
      </Text>
      <Text style={{ color: '#fff', display: 'block', marginBottom: 4 }}>
        Applied: {appliedLabels}
      </Text>
      <Text
        style={{ color: '#fff', display: 'block', marginBottom: 8 }}
        type="secondary"
      >
        The following dimensions could not be auto-applied:
      </Text>
      <ul style={{ margin: 0, paddingLeft: 16 }}>
        {warnings.map((w, idx) => (
          <li key={idx} style={{ color: '#fff', marginBottom: 2 }}>
            <Text style={{ color: '#fff' }}>
              {getSourceFacetLabel(w.source_facet || w.dimension_name)} -{' '}
              {getWarningReason(w)}
            </Text>
          </li>
        ))}
      </ul>
      <Text
        style={{ color: '#fff', display: 'block', marginTop: 8, fontSize: 11 }}
      >
        Head over to MIDAS to enable these dimensions for this metric.
      </Text>
    </div>
  );

  return (
    <Tooltip title={tooltipContent} color="#333">
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '4px 8px',
          backgroundColor: '#fff7e6',
          border: '1px solid #faad14',
          borderRadius: 4,
          fontSize: 12,
          color: '#ad6800',
          cursor: 'help',
        }}
      >
        <WarningAltOutlined style={{ fontSize: 12 }} />
        Population filters partially applied
        <MoreInfoOutlined style={{ fontSize: 10, marginLeft: 2 }} />
      </div>
    </Tooltip>
  );
};

// Failed badge - no constraints could be applied
const FailedStatusBadge: React.FC<{
  warnings: PrepopulateWarning[];
}> = ({ warnings }) => {
  const tooltipContent = (
    <div style={{ maxWidth: 300 }}>
      <Text strong style={{ color: '#fff', display: 'block', marginBottom: 8 }}>
        Failed to apply population filters
      </Text>
      <Text
        style={{ color: '#fff', display: 'block', marginBottom: 8 }}
        type="secondary"
      >
        The following dimensions could not be auto-applied:
      </Text>
      <ul style={{ margin: 0, paddingLeft: 16 }}>
        {warnings.map((w, idx) => (
          <li key={idx} style={{ color: '#fff', marginBottom: 2 }}>
            <Text style={{ color: '#fff' }}>
              {getSourceFacetLabel(w.source_facet || w.dimension_name)} -{' '}
              {getWarningReason(w)}
            </Text>
          </li>
        ))}
      </ul>
      <Text
        style={{ color: '#fff', display: 'block', marginTop: 8, fontSize: 11 }}
      >
        Head over to MIDAS to enable these dimensions for this metric.
      </Text>
    </div>
  );

  return (
    <Tooltip title={tooltipContent} color="#333">
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '4px 8px',
          backgroundColor: '#fff1f0',
          border: '1px solid #ff4d4f',
          borderRadius: 4,
          fontSize: 12,
          color: '#cf1322',
          cursor: 'help',
        }}
      >
        <WarningOutlined style={{ fontSize: 12 }} />
        Failed to apply population filters
        <MoreInfoOutlined style={{ fontSize: 10, marginLeft: 2 }} />
      </div>
    </Tooltip>
  );
};

// Render consolidated status badge for a metric
export const renderPopulationFilterBadges = (
  metric: MetricWithDimensionFields,
): React.ReactNode => {
  const status = getPopulationFilterStatus(metric);
  const appliedFilters = metric.populationDerivedFilters || [];
  const warnings = metric.prepopulationWarnings || [];

  if (status === 'none') {
    return null;
  }

  return (
    <Flex
      wrap="wrap"
      gap={8}
      style={{ marginTop: 4, marginBottom: 4, marginLeft: 48 }}
    >
      {status === 'success' && (
        <SuccessStatusBadge appliedFilters={appliedFilters} />
      )}
      {status === 'partial' && (
        <PartialStatusBadge
          appliedFilters={appliedFilters}
          warnings={warnings}
        />
      )}
      {status === 'failed' && <FailedStatusBadge warnings={warnings} />}
    </Flex>
  );
};
